import React from "react";
import CircularProgress from "@mui/material/CircularProgress";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "destructive"
  | "default"
  | "contextual"
  | "link"
  | "quiet-link";
export type ButtonSize = "xs" | "sm" | "md" | "lg";

export interface ButtonProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "onClick" | "type"
> {
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  loading?: boolean;
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  type?: "button" | "submit" | "reset";
  className?: string;
  icon?: React.ReactNode;
  ref?: React.Ref<HTMLButtonElement>;
}

const SPINNER_SIZES = {
  xs: 12,
  sm: 14,
  md: 16,
  lg: 18,
} as const;

const ICON_SIZES: Record<ButtonSize, string> = {
  xs: "icon-xs",
  sm: "icon-sm",
  md: "icon-md",
  lg: "icon-lg",
};

export const Button = ({
  children,
  onClick,
  disabled = false,
  loading = false,
  variant = "primary",
  size = "md",
  fullWidth = false,
  type = "button",
  className = "",
  icon,
  ref,
  ...restProps
}: ButtonProps) => {
  const childrenArray = React.Children.toArray(children);
  const hasText = childrenArray.some(
    (child) => typeof child === "string" && child.trim() !== "",
  );
  const hasElement = childrenArray.some((child) => React.isValidElement(child));

  const isIconOnly = (!!icon || hasElement) && !hasText;

  const getVariantClasses = (variant: ButtonVariant): string => {
    if (isIconOnly) {
      switch (variant) {
        case "primary":
          return "action-button-primary";
        case "secondary":
          return "action-button-secondary";
        case "default":
          return "action-icon-button-default";
        case "contextual":
          return "action-icon-button-contextual";
        default:
          return "action-icon-button-default";
      }
    }

    switch (variant) {
      case "primary":
        return "action-button-primary";
      case "secondary":
        return "action-button-secondary";
      case "destructive":
        return "action-button-destructive";
      case "link":
        return "action-button-link";
      case "quiet-link":
        return "action-button-quiet-link";
      default:
        return "action-button-secondary";
    }
  };

  const getSizeClasses = (size: ButtonSize): string => {
    if (isIconOnly) {
      switch (size) {
        case "xs":
          return "action-icon-button-xs";
        case "sm":
          return "action-icon-button-sm";
        case "md":
          return "action-icon-button-md";
        case "lg":
          return "action-icon-button-lg";
        default:
          return "action-icon-button-sm";
      }
    }

    switch (size) {
      case "xs":
      case "sm":
        return "action-button-sm";
      case "md":
        return "action-button-md";
      case "lg":
        return "action-button-lg";
      default:
        return "action-button-md";
    }
  };

  const baseClasses = "action-button-base";
  const variantClasses = getVariantClasses(variant);
  const sizeClasses = getSizeClasses(size);
  const widthClasses = fullWidth ? "w-full" : "";
  const disabledClasses = disabled || loading ? "action-button-disabled" : "";

  const allClasses = [
    baseClasses,
    variantClasses,
    sizeClasses,
    widthClasses,
    disabledClasses,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const spinnerColor =
    variant === "primary" || variant === "destructive"
      ? "white"
      : "var(--color-accent)";
  const isDisabled = disabled || loading;

  return (
    <button
      ref={ref}
      type={type}
      onClick={onClick}
      disabled={isDisabled}
      className={allClasses}
      aria-disabled={isDisabled}
      {...restProps}
    >
      {loading && (
        <span className="action-button-spinner" aria-hidden="true">
          <CircularProgress
            size={SPINNER_SIZES[size]}
            sx={{ color: spinnerColor }}
          />
        </span>
      )}
      <span
        className={`inline-flex items-center justify-center gap-2 whitespace-nowrap ${loading ? "opacity-0" : ""}`}
      >
        {icon && (
          <span className={isIconOnly ? "" : ICON_SIZES[size]}>{icon}</span>
        )}
        {children}
      </span>
    </button>
  );
};
